"use client";

import React from "react";
import { ArrowRight, ShieldCheck, Truck, Wrench } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const specifications = [{
  id: 1,
  icon: Wrench,
  label: "Catalog Range",
  value: "33 Products / 11 Categories"
}, {
  id: 2,
  icon: Truck,
  label: "Fulfillment",
  value: "Nationwide U.S. Shipping"
}, {
  id: 3,
  icon: ShieldCheck,
  label: "Quality Standard",
  value: "Entry to Premium Tier"
}];
export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  const transitionConfig = {
    duration: shouldReduceMotion ? 0 : 0.24,
    ease: [0.16, 1, 0.3, 1] as [number, number, number, number]
  };
  return <section id="hero" data-nav="dark" className="relative min-h-[85vh] lg:min-h-[90vh] flex items-center bg-background text-foreground overflow-hidden border-b border-border">
      {/* Background Image with Dark Industrial Overlays */}
      <div className="absolute inset-0 z-0">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1787497/img/industrial_workshop_hero_bg.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1787497/img/industrial_workshop_hero_bg.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1787497/img/industrial_workshop_hero_bg.png-wvc-sizes" alt="Dark industrial workshop with precision machinery and tools" loading="eager" decoding="async" className="w-full h-full object-cover object-center opacity-30 brightness-75 contrast-125" />
        {/* Cold-rolled steel gradient vignette */}
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/50" />
        {/* Subtle schematic grid overlay */}
        <div className="absolute inset-0 opacity-10 pointer-events-none bg-[radial-gradient(#94a3b8_1px,transparent_1px)] [background-size:24px_24px]" aria-hidden="true" />
      </div>

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32">
        <div className="max-w-3xl">
          {/* Technical Tag / Eyebrow */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={transitionConfig} className="flex items-center gap-3 mb-6">
            <Badge variant="outline" className="bg-card text-muted-foreground border-border font-mono text-xs uppercase tracking-widest px-3 py-1 rounded-sm">
              Industrial Grade Equipment
            </Badge>
            <span className="hidden sm:inline-block h-px w-8 bg-border" />
            <span className="hidden sm:inline-block font-mono text-xs text-muted-foreground tracking-wider uppercase">
              Est. Orange, CA
            </span>
          </motion.div>

          {/* Headline with Precision Spec Rule Marker */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          ...transitionConfig,
          delay: shouldReduceMotion ? 0 : 0.05
        }} className="relative pl-5 sm:pl-7 border-l-[3px] border-primary mb-6">
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight text-foreground leading-[1.05]">
              Heavy-duty tools for serious workshops
            </h1>
          </motion.div>

          {/* Technical Subheadline */}
          <motion.p initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          ...transitionConfig,
          delay: shouldReduceMotion ? 0 : 0.1
        }} className="font-sans text-lg sm:text-xl text-muted-foreground leading-relaxed mb-10 max-w-2xl">
            Equip your garage or fabrication facility with professional-grade machinery, hand tools, and maintenance gear engineered for rigorous duty cycles.
          </motion.p>

          {/* Dual Action CTA Buttons */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          ...transitionConfig,
          delay: shouldReduceMotion ? 0 : 0.15
        }} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mb-16">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-serif font-bold uppercase tracking-wider text-base h-12 px-8 shadow-sm transition-[background-color,transform] duration-150 active:scale-[0.98]">
              <Link to="/ShopPage" className="inline-flex items-center justify-center gap-2">
                <span>Shop Catalog</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="bg-secondary text-secondary-foreground border-border hover:bg-accent hover:text-accent-foreground hover:border-foreground/40 rounded-sm font-serif font-semibold uppercase tracking-wider text-base h-12 px-7 transition-[background-color,border-color,color] duration-150">
              <Link to="/ShopPage">
                View Categories
              </Link>
            </Button>
          </motion.div>

          {/* Technical Specification Bar */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          ...transitionConfig,
          delay: shouldReduceMotion ? 0 : 0.2
        }} className="pt-8 border-t border-border grid grid-cols-1 sm:grid-cols-3 gap-6">
            {specifications.map((spec, index) => {
            const IconComponent = spec.icon;
            return <div key={spec.id} data-index={index} className="flex items-start gap-3">
                  <div className="p-2 rounded-sm bg-card border border-border shrink-0 mt-0.5">
                    <IconComponent className="size-4 text-primary" />
                  </div>
                  <div>
                    <span className="block font-mono text-xs uppercase text-muted-foreground tracking-wider">
                      {spec.label}
                    </span>
                    <span className="block font-sans text-sm font-semibold text-foreground mt-0.5">
                      {spec.value}
                    </span>
                  </div>
                </div>;
          })}
          </motion.div>
        </div>
      </div>
    </section>;
}